import React, { useState, useEffect } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Grid,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { CustomModal } from "@/components/Modal";
import {
  type SavedMapPlotRequestDetail,
  type PlotRequestTag,
  type AdditionalFieldsFormData,
} from "@/features/map/types";
import { toast } from "react-toastify";
import { useForm, Controller, type Path } from "react-hook-form";
import { useBulkUpdatePlotRequestDetails } from "@/features/map/hooks/useBulkUpdatePlotRequestDetails";
import { CAutocompleteMultiFreeSoloTextField } from "@/components/TextField/CAutocompleteMultiFreeSoloTextField";
import { SizingWrapperStyle } from "@/components/Wrapper/SizingWrapper";
import { useAdditionalFieldsConfig } from "@/features/map/hooks/useAdditionalFieldsConfig";
import { parseAdditionalFields } from "@/features/map/utils/additionalFieldsHelpers";
import { CAdditionalFieldInput } from "@/features/map/components/CAdditionalFieldInput";
import {
  preventNonNumericValueWhenKeyDown,
  preventNonNumericValueWhenPaste,
} from "@/utils/utils";
import { validateCoordinates } from "@/features/mapPlotRequest/utils/validateCoordinates";
import { formatNumberWithComma } from "@/utils/formatNumber";

// 真偽値フィールド用の定数（再レンダリング時の再生成を防ぐ）
const BOOLEAN_OPTIONS = ["", "true", "false"] as const;
const getBooleanOptionLabel = (option: string): string => {
  if (option === "") return "更新しない";
  return option === "true" ? "はい" : "いいえ";
};

interface IProps {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  selectedDetails: SavedMapPlotRequestDetail[];
  availableTags: PlotRequestTag[];
  onSuccess: () => void;
}

interface FormData {
  realEstateType: "土地" | "建物" | "区分建物" | "";
  requestAddress: string;
  requestLocation: string;
  requestChiban: string;
  requestKaokuNumber: string;
  requestLatitude: string;
  requestLongitude: string;
  tags: string[];
  remarks: string;
  additionalFields?: AdditionalFieldsFormData;
}

export const BulkEditPlotDetailsModal: React.FC<IProps> = ({
  open,
  setOpen,
  selectedDetails,
  availableTags,
  onSuccess,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { trigger: bulkUpdateDetails } = useBulkUpdatePlotRequestDetails();
  const { availableFields, hasAvailableFields } = useAdditionalFieldsConfig();

  // 追加項目のデフォルト値を生成
  const additionalFieldsDefaultValues = React.useMemo(() => {
    if (!availableFields) return {};
    const defaults: AdditionalFieldsFormData = {};
    Object.keys(availableFields).forEach((key) => {
      defaults[key] = "";
    });
    return defaults;
  }, [availableFields]);

  const {
    register,
    handleSubmit: handleFormSubmit,
    reset,
    control,
    watch,
    setError,
    setValue,
    formState: { isValid },
  } = useForm<FormData>({
    mode: "onChange",
    defaultValues: {
      realEstateType: "",
      requestAddress: "",
      requestLocation: "",
      requestChiban: "",
      requestKaokuNumber: "",
      requestLatitude: "",
      requestLongitude: "",
      tags: [],
      remarks: "",
      additionalFields: {},
    },
  });

  const realEstateType = watch("realEstateType");

  // 種別変更時に非表示になるフィールドをクリア
  useEffect(() => {
    if (realEstateType === "土地") {
      setValue("requestKaokuNumber", "");
    } else if (realEstateType === "建物" || realEstateType === "区分建物") {
      setValue("requestChiban", "");
    }
  }, [realEstateType, setValue]);

  useEffect(() => {
    if (!open) return;

    if (selectedDetails.length === 1) {
      // 1件のみ選択時は初期値を設定
      const detail = selectedDetails[0];
      const parsedAdditionalFields = detail.additionalFields
        ? parseAdditionalFields(detail.additionalFields)
        : additionalFieldsDefaultValues;

      reset({
        realEstateType: detail.realEstateType ?? "土地",
        requestAddress: detail.requestAddress ?? "",
        requestLocation: detail.requestLocation ?? "",
        requestChiban: detail.requestChiban ?? "",
        requestKaokuNumber: detail.requestKaokuNumber ?? "",
        requestLatitude: detail.requestLatitude
          ? String(detail.requestLatitude)
          : "",
        requestLongitude: detail.requestLongitude
          ? String(detail.requestLongitude)
          : "",
        tags: detail.tags.map((tag) => tag.name),
        remarks: detail.remarks,
        additionalFields: parsedAdditionalFields,
      });
    } else {
      // 2件以上選択時は空にする
      reset({
        realEstateType: "",
        requestAddress: "",
        requestLocation: "",
        requestChiban: "",
        requestKaokuNumber: "",
        requestLatitude: "",
        requestLongitude: "",
        tags: [],
        remarks: "",
        additionalFields: additionalFieldsDefaultValues,
      });
    }
  }, [open, selectedDetails.length, reset, additionalFieldsDefaultValues]);

  const handleClose = (): void => {
    if (!isSubmitting) {
      setOpen(false);
      reset();
    }
  };

  const onSubmit = async (data: FormData): Promise<void> => {
    if (selectedDetails.length === 0) {
      toast.error("編集対象が選択されていません");
      return;
    }

    // 緯度・経度のパースとバリデーション
    const { latitude, longitude, isValid } = validateCoordinates(
      data.requestLatitude,
      data.requestLongitude,
      setError
    );

    if (!isValid) {
      return;
    }

    setIsSubmitting(true);
    try {
      // 追加項目の変換処理
      // 真偽値: true/falseが明示的に選択された場合のみ送信（選択なしは除外）
      // 他のフィールド: 空文字列は更新対象から除外
      let finalAdditionalFieldsData:
        | Record<string, { value: string }>
        | undefined;

      if (data.additionalFields && availableFields) {
        const result: Record<string, { value: string }> = {};

        for (const field of availableFields.fields) {
          const value = data.additionalFields[field.id] ?? "";

          if (
            field.fieldType === "boolean" &&
            (value === "true" || value === "false")
          ) {
            result[field.id] = { value };
          } else if (field.fieldType !== "boolean" && value !== "") {
            result[field.id] = { value };
          }
        }

        finalAdditionalFieldsData =
          Object.keys(result).length > 0 ? result : undefined;
      }

      // 入力された値のみを更新対象とする
      const updates = selectedDetails.map((detail) => ({
        id: detail.id,
        ...(data.realEstateType && { realEstateType: data.realEstateType }),
        ...(data.requestAddress && { requestAddress: data.requestAddress }),
        ...(data.requestLocation && { requestLocation: data.requestLocation }),
        ...(data.requestChiban && { requestChiban: data.requestChiban }),
        ...(data.requestKaokuNumber && {
          requestKaokuNumber: data.requestKaokuNumber,
        }),
        ...(latitude !== null && { requestLatitude: latitude }),
        ...(longitude !== null && { requestLongitude: longitude }),
        ...(data.tags.length > 0 && { tags: data.tags }),
        ...(data.remarks && { remarks: data.remarks }),
        ...(finalAdditionalFieldsData !== undefined && {
          additionalFields: finalAdditionalFieldsData,
        }),
      }));

      await bulkUpdateDetails({ details: updates });

      toast.success(
        `${formatNumberWithComma(selectedDetails.length)}件の行を更新しました`
      );
      onSuccess();
      handleClose();
    } catch (error) {
      toast.error("一括編集に失敗しました");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <CustomModal
      isOpen={open}
      handleClose={handleClose}
      ariaLabelledby="bulk-edit-plot-details-modal"
      ariaDescribedby="bulk-edit-plot-details-modal-description"
      width="600px"
    >
      <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
        {/* 固定ヘッダー */}
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
          <Typography variant="h6">一括で編集</Typography>
          <Button onClick={handleClose} disabled={isSubmitting}>
            × 閉じる
          </Button>
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          選択された{formatNumberWithComma(selectedDetails.length)}
          件の行を一括編集します。入力した項目のみが更新されます。
        </Typography>

        {/* スクロール可能なコンテンツエリア */}
        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            maxHeight: "calc(90vh - 200px)",
            pr: 1,
            pt: 1,
          }}
        >
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                select
                fullWidth
                label="種別"
                {...register("realEstateType")}
                disabled={isSubmitting}
                value={realEstateType}
              >
                <MenuItem value="">選択してください</MenuItem>
                <MenuItem value="土地">土地</MenuItem>
                <MenuItem value="建物">建物</MenuItem>
                <MenuItem value="区分建物">区分建物</MenuItem>
              </TextField>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="住居表示"
                placeholder="住居表示を入力"
                {...register("requestAddress")}
                disabled={isSubmitting}
                inputProps={{ maxLength: 256 }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="所在"
                placeholder="所在を入力"
                {...register("requestLocation")}
                disabled={isSubmitting}
                inputProps={{ maxLength: 256 }}
              />
            </Grid>

            {realEstateType === "土地" ? (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="地番"
                  placeholder="地番を入力"
                  {...register("requestChiban")}
                  disabled={isSubmitting}
                  inputProps={{ maxLength: 256 }}
                />
              </Grid>
            ) : realEstateType === "建物" || realEstateType === "区分建物" ? (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="家屋番号"
                  placeholder="家屋番号を入力"
                  {...register("requestKaokuNumber")}
                  disabled={isSubmitting}
                  inputProps={{ maxLength: 256 }}
                />
              </Grid>
            ) : (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="地番"
                  placeholder="地番を入力"
                  {...register("requestChiban")}
                  disabled={isSubmitting}
                  inputProps={{ maxLength: 256 }}
                />
              </Grid>
            )}

            <Grid item xs={6}>
              <TextField
                fullWidth
                label="緯度"
                placeholder="緯度を入力"
                type="number"
                {...register("requestLatitude")}
                disabled={isSubmitting}
                onKeyDown={preventNonNumericValueWhenKeyDown}
                onPaste={preventNonNumericValueWhenPaste}
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                fullWidth
                label="経度"
                placeholder="経度を入力"
                type="number"
                {...register("requestLongitude")}
                disabled={isSubmitting}
                onKeyDown={preventNonNumericValueWhenKeyDown}
                onPaste={preventNonNumericValueWhenPaste}
              />
            </Grid>

            <Grid item xs={12}>
              <CAutocompleteMultiFreeSoloTextField
                control={control}
                name="tags"
                label="タグ"
                disabled={isSubmitting}
                options={availableTags.map((tag) => tag.name)}
                size={SizingWrapperStyle.INHERIT}
                maxLength={20}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="備考"
                placeholder="備考を入力"
                multiline
                rows={3}
                {...register("remarks")}
                disabled={isSubmitting}
                inputProps={{ maxLength: 200 }}
                helperText={`${watch("remarks")?.length ?? 0}/200`}
              />
            </Grid>

            {hasAvailableFields && availableFields && (
              <>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" sx={{ mt: 1, mb: 1 }}>
                    カスタム項目
                  </Typography>
                </Grid>
                {availableFields.fields.map((field) =>
                  field.fieldType === "boolean" ? (
                    // 真偽値フィールドは3択Autocompleteで実装
                    // 理由: 一括編集では「変更しない」という選択肢が必要
                    // - 更新しない: 既存の値を維持（変更しない）
                    // - はい: 全ての選択行をtrueに変更
                    // - いいえ: 全ての選択行をfalseに変更
                    // ※ 通常のCheckboxだと「変更しない」が表現できないため、Autocompleteを使用
                    <Grid item xs={12} key={field.id}>
                      <Controller
                        name={`additionalFields.${field.id}` as Path<FormData>}
                        control={control}
                        render={({ field: { value, onChange } }) => (
                          <Autocomplete
                            options={BOOLEAN_OPTIONS}
                            getOptionLabel={getBooleanOptionLabel}
                            value={typeof value === "string" ? value : ""}
                            onChange={(_, newValue) => {
                              onChange(newValue ?? "");
                            }}
                            disabled={isSubmitting}
                            renderInput={(params) => (
                              <TextField {...params} label={field.key} />
                            )}
                          />
                        )}
                      />
                    </Grid>
                  ) : (
                    <Grid item xs={12} key={field.id}>
                      <CAdditionalFieldInput
                        control={control}
                        name={`additionalFields.${field.id}` as Path<FormData>}
                        label={field.key}
                        fieldType={field.fieldType}
                        disabled={isSubmitting}
                      />
                    </Grid>
                  )
                )}
              </>
            )}
          </Grid>
        </Box>

        <Stack
          direction="row"
          justifyContent="center"
          spacing={2}
          sx={{ mt: 3 }}
        >
          <Button
            variant="contained"
            color="primary"
            onClick={(e) => {
              void handleFormSubmit(onSubmit)(e);
            }}
            disabled={isSubmitting || !isValid}
          >
            登録する
          </Button>
        </Stack>
      </Box>
    </CustomModal>
  );
};
